import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Plus,
} from "lucide-react";
import {
  API,
  ROLE_OPTIONS,
} from "./shared";

export function TeamTab({ authHeader }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", role: "nurse", password: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/staff`, { headers: authHeader() });
      setStaff(res.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMsg("");
    try {
      const res = await axios.post(`${API}/admin/staff`,
        { ...form, origin_url: window.location.origin }, { headers: authHeader() });
      const invited = res.data.invite_email === "sent";
      setMsg(invited
        ? `${form.full_name} added — an email with their sign-in details was sent to ${form.email}.`
        : `${form.full_name} added. The invite email couldn't be delivered right now (email service is being restored) — share these sign-in details with them directly: portal ${window.location.origin}/admin · email ${form.email} · temporary password ${form.password}`);
      setForm({ full_name: "", email: "", role: "nurse", password: "" });
      await load();
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not create the account.");
    } finally { setSaving(false); }
  };

  const patch = async (id, body) => {
    try {
      await axios.patch(`${API}/admin/staff/${id}`, body, { headers: authHeader() });
      await load();
      return true;
    } catch { return false; }
  };

  const resetPassword = async (s) => {
    const temp = `Soar${Math.random().toString(36).slice(2, 8)}!${Math.floor(Math.random() * 90 + 10)}`;
    if (await patch(s.id, { password: temp })) {
      setMsg(`Temporary password for ${s.email}: ${temp} — copy it now, it won't be shown again.`);
    }
  };

  const resendInvite = async (s) => {
    try {
      const res = await axios.post(`${API}/admin/staff/${s.id}/resend-invite`,
        { origin_url: window.location.origin }, { headers: authHeader() });
      const d = res.data;
      setMsg(d.invite_email === "sent"
        ? `Invite emailed to ${s.email} with a new temporary password.`
        : `Email couldn't be delivered right now — share manually: portal ${window.location.origin}/admin · email ${s.email} · temporary password ${d.temp_password}`);
      await load();
    } catch {
      setMsg("Couldn't resend the invite.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/staff/${id}`, { headers: authHeader() });
      await load();
    } catch {}
  };

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="team-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Team & Roles</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Add staff accounts with role-based access — each person sees only the areas their role allows.
        </p>
      </div>

      <form onSubmit={create} data-testid="staff-form"
        className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="staff-name" required placeholder="Full name" value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
        <input data-testid="staff-email" required type="email" placeholder="Work email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <select data-testid="staff-role" value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
          {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="staff-password" required type="text" minLength={8}
          placeholder="Temporary password (min 8 chars)" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        <div className="sm:col-span-2">
          <button type="submit" data-testid="staff-create-btn" disabled={saving}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add team member
          </button>
        </div>
      </form>

      {msg && (
        <div data-testid="team-msg" className="mb-5 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-4 text-sm text-[#3F5043] break-all">
          {msg}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : staff.length === 0 ? (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center text-sm text-[#899480]" data-testid="team-empty">
          No staff accounts yet — add your first team member above.
        </div>
      ) : (
        <div className="space-y-2">
          {staff.map((s) => (
            <div key={s.id} data-testid={`staff-row-${s.id}`}
              className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#3F5043]">{s.full_name}
                  <span className={`ml-2 text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full ${
                    s.active ? "bg-[#899480]/15 text-[#3F5043]" : "bg-red-100 text-red-700"}`}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="text-xs text-[#899480]">{s.email}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={s.role} data-testid={`staff-role-select-${s.id}`}
                  onChange={(e) => patch(s.id, { role: e.target.value })}
                  className="bg-[#EDEDEE] rounded-full px-3 py-1.5 text-xs text-[#3F5043] focus:outline-none">
                  {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button onClick={() => patch(s.id, { active: !s.active })} data-testid={`staff-toggle-${s.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                  {s.active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => patch(s.id, { bookable: !s.bookable })} data-testid={`staff-bookable-${s.id}`}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    s.bookable
                      ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#875F45]"
                      : "border-[#BCB3A6]/50 text-[#899480] hover:bg-[#899480]/10"}`}>
                  {s.bookable ? "Bookable online ✓" : "Book online: off"}
                </button>
                <button onClick={() => resetPassword(s)} data-testid={`staff-reset-${s.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/10 transition-colors">
                  Reset password
                </button>
                <button onClick={() => resendInvite(s)} data-testid={`staff-resend-invite-${s.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                  Resend invite
                </button>
                <button onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`}
                  className="text-xs rounded-full px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 transition-colors">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
